// Browser (Web Push) notifications. The service worker lives at /sw.js
// (public/sw.js) and shows notifications even when no tab is open.

import { api } from "../api/endpoints";

const DEVICE_KEY = "br.pushDevice";

export type PushSupport =
  | { ok: true }
  | { ok: false; reason: "insecure" | "unsupported" | "ios-install" | "denied" };

function isIos(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

function isStandalone(): boolean {
  return window.matchMedia("(display-mode: standalone)").matches || (navigator as { standalone?: boolean }).standalone === true;
}

export function pushSupport(): PushSupport {
  if (!window.isSecureContext) return { ok: false, reason: "insecure" };
  const hasApis = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
  if (!hasApis) return { ok: false, reason: isIos() && !isStandalone() ? "ios-install" : "unsupported" };
  if (Notification.permission === "denied") return { ok: false, reason: "denied" };
  return { ok: true };
}

export function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

interface StoredDevice {
  id: string;
  endpoint: string;
}

function readStored(): StoredDevice | null {
  try {
    const raw = window.localStorage.getItem(DEVICE_KEY);
    return raw ? (JSON.parse(raw) as StoredDevice) : null;
  } catch {
    return null;
  }
}

function writeStored(d: StoredDevice | null): void {
  try {
    if (d) window.localStorage.setItem(DEVICE_KEY, JSON.stringify(d));
    else window.localStorage.removeItem(DEVICE_KEY);
  } catch {
    // Storage blocked: registration still works, we just can’t remember the id.
  }
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration> {
  const reg = await navigator.serviceWorker.register("/sw.js", { scope: "/" });
  await navigator.serviceWorker.ready;
  return reg;
}

export async function currentSubscription(): Promise<PushSubscription | null> {
  if (!("serviceWorker" in navigator)) return null;
  const reg = await navigator.serviceWorker.getRegistration("/");
  if (!reg) return null;
  return reg.pushManager.getSubscription();
}

/** Ask permission, subscribe, and register this browser with the server. */
export async function enablePush(vapidPublicKey: string): Promise<void> {
  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error(
      permission === "denied"
        ? "Notifications are blocked for this site. Allow them in your browser’s site settings, then try again."
        : "Notification permission was not granted.",
    );
  }
  const reg = await registerServiceWorker();
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
    });
  }
  const json = sub.toJSON();
  const p256dh = json.keys?.p256dh;
  const auth = json.keys?.auth;
  if (!json.endpoint || !p256dh || !auth) throw new Error("The browser returned an incomplete push subscription.");
  const res = await api.registerDevice({ platform: "web", subscription: { endpoint: json.endpoint, keys: { p256dh, auth } } });
  writeStored({ id: res.id, endpoint: json.endpoint });
}

/** Unsubscribe this browser and remove its registration from the server. */
export async function disablePush(): Promise<void> {
  const stored = readStored();
  const sub = await currentSubscription();
  if (stored) await api.deleteDevice(stored.id).catch(() => undefined);
  if (sub) await sub.unsubscribe().catch(() => false);
  writeStored(null);
}

/** Used on sign-out so the next person on this browser does not get our alerts. */
export async function forgetPushDevice(): Promise<void> {
  if (!readStored()) return;
  await disablePush();
}

export function storedPushDevice(): StoredDevice | null {
  return readStored();
}
